import { GoogleAuthProvider, signInWithPopup, signInWithRedirect } from 'firebase/auth';
import { getAuthClient } from '@talk2nick/shared';
import { isInAppBrowser, openExternalBrowser } from '@talk2nick/core';

/**
 * Google 로그인 (PLAN 5-1장).
 *
 * 모바일에서는 팝업이 차단되거나 빈 탭이 남는 경우가 많아 리디렉션을 쓴다.
 * 팝업이 차단된 데스크톱 환경도 리디렉션으로 폴백한다.
 */

/** 리디렉션이 사실상 유일한 경로인 환경 판별 */
export function isMobileBrowser(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
}

/** 취소는 오류로 취급하지 않는다 (사용자 의도) */
export function isCancelled(error: unknown): boolean {
  const code = (error as { code?: string }).code;
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
}

export function isPopupBlocked(error: unknown): boolean {
  const code = (error as { code?: string }).code;
  return (
    code === 'auth/popup-blocked' ||
    code === 'auth/operation-not-supported-in-this-environment' ||
    code === 'auth/internal-error'
  );
}

/**
 * @param invitedEmail 특정 초대장에 바인딩된 고정 Gmail 계정 (선택)
 * @returns 로그인 흐름이 시작되었으면 true, 사용자가 취소했으면 false.
 */
export async function signInWithGoogle(invitedEmail?: string): Promise<boolean> {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (typeof window !== 'undefined' && isInAppBrowser(userAgent)) {
    const escaped = openExternalBrowser(window.location.href, userAgent);
    if (escaped) return true;
  }

  const auth = getAuthClient();
  const provider = new GoogleAuthProvider();
  // 여러 Google 계정을 쓰는 사용자를 위해 계정 선택 화면을 띄우되, 초대장이면 해당 계정을 힌트로 고정
  provider.setCustomParameters({
    prompt: 'select_account',
    ...(invitedEmail ? { login_hint: invitedEmail } : {}),
  });

  try {
    await signInWithPopup(auth, provider);
    return true;
  } catch (error) {
    if (isCancelled(error)) return false;
    if (isPopupBlocked(error) || isMobileBrowser()) {
      console.warn('[talk2nIcK] 팝업 로그인 실패, 리디렉션 폴백 시도:', error);
      try {
        await signInWithRedirect(auth, provider);
        return true;
      } catch (redirectError) {
        if (isCancelled(redirectError)) return false;
        throw redirectError;
      }
    }
    throw error;
  }
}
